/**
 * Servicio para manejar configuraciones locales del usuario
 * Usa localStorage para persistir datos entre sesiones
 */

class LocalService {
  
  /**
   * Claves para localStorage
   */
  static KEYS = {
    SELECTED_LANGUAGE: 'selectedLanguage',
  }

  /**
   * Obtener idioma seleccionado por el usuario
   * @returns {string} Código del idioma ('es', 'en', etc.)
   */
  getSelectedLanguage() {
    try {
      const language = localStorage.getItem(LocalService.KEYS.SELECTED_LANGUAGE)
      console.log('📖 Idioma obtenido del localStorage:', language)
      return language || 'es' // Por defecto español
    } catch (error) {
      console.error('❌ Error obteniendo idioma del localStorage:', error)
      return 'es'
    }
  }

  /**
   * Guardar idioma seleccionado por el usuario
   * @param {string} languageCode - Código del idioma
   */
  setSelectedLanguage(languageCode) {
    try {
      localStorage.setItem(LocalService.KEYS.SELECTED_LANGUAGE, languageCode)
      console.log('💾 Idioma guardado en localStorage:', languageCode)
      
      // También actualizar el atributo lang del documento para accesibilidad
      document.documentElement.lang = languageCode
      
      return true
    } catch (error) {
      console.error('❌ Error guardando idioma en localStorage:', error)
      return false
    }
  }

  /**
   * Limpiar todos los datos locales
   */
  clearAllData() {
    try {
      Object.values(LocalService.KEYS).forEach(key => {
        localStorage.removeItem(key)
      })
      console.log('🧹 Datos locales limpiados')
      return true
    } catch (error) {
      console.error('❌ Error limpiando datos locales:', error)
      return false
    }
  }

  /**
   * Verificar si localStorage está disponible
   * @returns {boolean}
   */
  isStorageAvailable() {
    try {
      const testKey = '__localStorage_test__'
      localStorage.setItem(testKey, 'test')
      localStorage.removeItem(testKey)
      return true
    } catch (error) {
      console.warn('⚠️ localStorage no está disponible:', error)
      return false
    }
  }

  /**
   * Obtener información de debug sobre el almacenamiento local
   * @returns {object}
   */
  getDebugInfo() {
    return {
      isAvailable: this.isStorageAvailable(),
      selectedLanguage: this.getSelectedLanguage(),
      storageUsage: this.getStorageUsage()
    }
  }

  /**
   * Obtener uso aproximado del localStorage
   * @returns {object}
   */
  getStorageUsage() {
    try {
      let totalSize = 0
      const details = {}
      
      Object.values(LocalService.KEYS).forEach(key => {
        const value = localStorage.getItem(key)
        const size = value ? new Blob([value]).size : 0
        details[key] = `${size} bytes`
        totalSize += size
      })
      
      return {
        total: `${totalSize} bytes`,
        details
      }
    } catch (error) {
      console.error('❌ Error calculando uso del localStorage:', error)
      return { total: 'Unknown', details: {} }
    }
  }
}

// Crear instancia única del servicio
const localService = new LocalService()

export default localService
